import React, { useState, useEffect, useContext } from "react";
import { FaRegCopy, FaCheckCircle } from "react-icons/fa";
import { ThemeContext } from "../../contexts/themeContext";
import { DataGrid } from "../../common";
import "../../assets/styles/card-blacklist.scss";
import { APIEndpoint } from "../../utils/axios";

// Helper component for truncated text with a hover tooltip and copy to clipboard button
const CopyableTruncatedCell = ({
  text,
  tooltipText,
  maxLength = 50,
  className = "",
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const displayText =
    text && text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text || "";

  return (
    <div className={`copyable-truncated-cell ${className}`}>
      <button
        type="button"
        className={`copy-btn ${copied ? "copied" : ""}`}
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy to clipboard"}
      >
        {copied ? (
          <FaCheckCircle size={11} color="#28a745" />
        ) : (
          <FaRegCopy size={11} />
        )}
      </button>
      <span className="cell-text" title={tooltipText || text}>
        {displayText}
      </span>
    </div>
  );
};

export default function CardBlacklistLogs() {
  const { themedColor } = useContext(ThemeContext);

  const [logs, setLogs] = useState([]);

  const fetchLogs = async () => {
    try {
      const res = await APIEndpoint.get("payments/card/blacklist/logs");
      const items =
        res?.items || res?.data?.items || (Array.isArray(res) ? res : []);
      const formatted = items.map((it, idx) => ({
        ...it,
        cardHash: it.cardHash || it.hash || it.card_hash || it.key || "",
        hash: it.hash || it.cardHash || it.card_hash || it.key || "",
      }));
      setLogs(formatted);
    } catch (err) {
      console.error("Error fetching card blacklist logs:", err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Ag-Grid columns renderer mappings
  const EventTypeRenderer = (params) => {
    if (!params.data) return null;
    return (
      <span className={`badge-event ${params.data.event}`}>
        {params.data.event.replace("_", " ")}
      </span>
    );
  };

 

  const AuditColumns = [
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 1,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "",
    },
    {
      field: "cardNo",
      headerName: "Card Number (Masked)",
      flex: 1.5,
      cellRenderer: (params) => {
        return (
          <div className="card-display">
        <span className="card-numbers">{params.data.cardNo}</span>
      </div>
        );
      },
    },
     {
      field: "cardHash",
      headerName: "Card Hash",
      flex: 1.5,
      cellRenderer: (params) => {
        return (
          <CopyableTruncatedCell
            text={params.data?.cardHash || ""}
            tooltipText={params.data?.cardHash || ""}
            maxLength={20}
          />
        );
      },
    },
    {
      field: "user",
      headerName: "Blacklisted By",
      flex: 1.2,
    },
    {
      field: "event",
      headerName: "Event Type",
      flex: 1,
      cellRenderer: EventTypeRenderer,
    },
    {
      field: "details",
      headerName: "Log Detail Message",
      flex: 2,
      cellRenderer: (params) => {
        return (
          <CopyableTruncatedCell
            text={params.data?.details || ""}
            tooltipText={params.data?.details || ""}
          />
        );
      },
    },
  ];

  const isDark = themedColor?.sidebar === "#181818";

  return (
    <div className={`blacklist-container ${isDark ? "dark" : "light"}`}>
      <div className="flexCol full-flex ag-theme-alpine-dark">
        <DataGrid
          className="full-flex"
          columns={AuditColumns}
          showDefaultFilters={true}
          enableGridSearch={true}
          rowModelType="clientSide"
          rowData={logs}
          columnResize={true}
          pagination={true}
          paginationPageSize={10}
          suppressPaginationPanel={false}
        />
      </div>
    </div>
  );
}
